import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import AlertModal from '../components/AlertModal';
import PremiumSelect from '../components/PremiumSelect';
import { User, Cycle, AlertState } from '../types';
import './CreateFeedback.css';

const yesNoPartialOptions = [
  { value: 'sim', label: 'Sim' },
  { value: 'parcialmente', label: 'Parcialmente' },
  { value: 'não', label: 'Não' },
];

interface ReverseFeedbackFormData {
  managerId: string;
  cycleId: string;
  clarityScore: number;
  clarityQuestion: 'sim' | 'parcialmente' | 'não';
  clarityObservations: string;
  availabilityScore: number;
  availabilityQuestion: 'sim' | 'parcialmente' | 'não';
  availabilityObservations: string;
  feedbackQualityScore: number;
  feedbackQualityQuestion: 'sim' | 'parcialmente' | 'não';
  feedbackQualityObservations: string;
  whatManagerDoesWell: string;
  whatCanImprove: string;
  suggestions: string;
  anonymous: boolean;
}

const CreateReverseFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<ReverseFeedbackFormData>({
    managerId: '',
    cycleId: '',
    clarityScore: 3,
    clarityQuestion: 'sim',
    clarityObservations: '',
    availabilityScore: 3,
    availabilityQuestion: 'sim',
    availabilityObservations: '',
    feedbackQualityScore: 3,
    feedbackQualityQuestion: 'sim',
    feedbackQualityObservations: '',
    whatManagerDoesWell: '',
    whatCanImprove: '',
    suggestions: '',
    anonymous: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      const [usersRes, cyclesRes] = await Promise.all([
        api.get<User[]>('/users'),
        api.get<Cycle[]>('/cycles'),
      ]);
      setAllUsers(usersRes.data.filter((u) => u.active));
      setCycles(cyclesRes.data.filter((c) => c.active));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const cycleSelectOptions = useMemo(
    () => [
      { value: '', label: 'Selecione...' },
      ...cycles.map((cycle) => ({ value: cycle.id, label: cycle.name })),
    ],
    [cycles],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | { target: { name: string; value: string } }): void => {
    const { name, value } = e.target;
    const type = 'type' in e.target ? (e.target as HTMLInputElement).type : undefined;
    const checked = 'checked' in e.target ? (e.target as HTMLInputElement).checked : false;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name.includes('Score')
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/feedbacks/reverse', formData);
      setAlert({
        isOpen: true,
        message: 'Feedback reverso criado com sucesso!',
        type: 'success',
      });
      setTimeout(() => {
        navigate('/feedbacks');
      }, 1500);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao criar feedback reverso',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>Criar Feedback Reverso</h1>
      <form onSubmit={handleSubmit} className="feedback-form">
        <div className="form-section">
          <h2>Informações Básicas</h2>
          <SearchableSelect
            options={allUsers}
            value={formData.managerId}
            onChange={handleChange}
            placeholder="Selecione o usuário a ser avaliado..."
            label="Usuário a Avaliar *"
            required
            wrapperClassName="premium-select-field"
          />
          <PremiumSelect
            label="Ciclo"
            wrapperClassName="premium-select-field"
            value={formData.cycleId}
            options={cycleSelectOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                cycleId: value,
              }))
            }
            placeholder="Selecione..."
            required
          />
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="anonymous"
                checked={formData.anonymous}
                onChange={handleChange}
              />
              Feedback Anônimo
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Clareza Técnica e Direcionamento</h2>
          <div className="form-group">
            <label>Score de Clareza (1-5) *</label>
            <input
              type="number"
              name="clarityScore"
              value={formData.clarityScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <PremiumSelect
            label="O gestor deixou claro o que deveria ser feito?"
            wrapperClassName="premium-select-field"
            value={formData.clarityQuestion}
            options={yesNoPartialOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                clarityQuestion: value as ReverseFeedbackFormData['clarityQuestion'],
              }))
            }
            required
          />
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="clarityObservations"
              value={formData.clarityObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Disponibilidade e Suporte</h2>
          <div className="form-group">
            <label>Score de Disponibilidade (1-5) *</label>
            <input
              type="number"
              name="availabilityScore"
              value={formData.availabilityScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <PremiumSelect
            label="O gestor ajudou a resolver bloqueios?"
            wrapperClassName="premium-select-field"
            value={formData.availabilityQuestion}
            options={yesNoPartialOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                availabilityQuestion: value as ReverseFeedbackFormData['availabilityQuestion'],
              }))
            }
            required
          />
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="availabilityObservations"
              value={formData.availabilityObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Qualidade do Feedback e Desenvolvimento</h2>
          <div className="form-group">
            <label>Score de Qualidade do Feedback (1-5) *</label>
            <input
              type="number"
              name="feedbackQualityScore"
              value={formData.feedbackQualityScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <PremiumSelect
            label="O feedback ajudou você a evoluir?"
            wrapperClassName="premium-select-field"
            value={formData.feedbackQualityQuestion}
            options={yesNoPartialOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                feedbackQualityQuestion: value as ReverseFeedbackFormData['feedbackQualityQuestion'],
              }))
            }
            required
          />
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="feedbackQualityObservations"
              value={formData.feedbackQualityObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Consolidado</h2>
          <div className="form-group">
            <label>O que o gestor faz bem *</label>
            <textarea
              name="whatManagerDoesWell"
              value={formData.whatManagerDoesWell}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>O que pode melhorar *</label>
            <textarea
              name="whatCanImprove"
              value={formData.whatCanImprove}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Sugestões Práticas *</label>
            <textarea
              name="suggestions"
              value={formData.suggestions}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/feedbacks')} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Feedback Reverso'}
          </button>
        </div>
      </form>
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default CreateReverseFeedback;
